import {
    useSWRPaginationTemplate,
    useSWRTemplate,
} from 'hooks/dataHooksTemplate';
import {useAuth} from 'hooks/useAuth';
import {get, sortBy, reverse, orderBy, flatten, keys} from 'lodash';
import {cleanObjectNilValue} from 'utils/clean';
import {api} from './common';
import {processClosedOrders, processForexAccountList} from 'data/forex';
import {formatedStartEndDate, processStartEndDateLocale} from 'utils/time';
import {
    formatMonthDataAndYear,
    formatDateInTimezone,
    getLocalTime,
    currencyFormatorWithDecimal,
    formatDateTimes
} from 'utils/formator';
import {
    gatewayDisabled,
    paymentWaysIdChannelMap,
    paymentWaysNameChannelMap,
} from 'data/paymentGateway';
import client from 'helper/httpClient';
import { usePreference } from 'hooks/usePreference';
import { useUserPreference } from './settings';
import Excel from 'exceljs';
import numeral from 'numeral';
import { convertSource } from 'page/app/ConstantGroup';

export const useCommonsAccountList = (options = {}) => {
    const {
        type = null,
        user,
        enableOnly,
        login = null,
        request = true,
    } = options;

    const swr = useSWRTemplate({
        key: request
                ? [
                    'commonsAccountList',
                    type,
                    user,
                    login,
                    enableOnly,
                ] : null,
        request: async ([
                            _,
                            _type,
                            _user,
                            _login,
                            _enableOnly,
                        ]) => {
            const {result} = await api().get(
                ['commons', 'account-list'],
                cleanObjectNilValue({
                    type: _type != 'Hidden' ? _type : null,
                    user: _user,
                    login: _login,
                }),
            );

            return (result || []).map((item)=>(
                {
                    ...item,
                    label: `${get(item, 'login')} ${get(item, 'source')} ${get(
                        item,
                        'currency',
                    )}`,
                    value:  `${get(item, 'login')},${get(item, 'id')},${get(item, ':user.name')},${get(item, ':user.id')}`,
                }
            ))
                .filter((item) =>
                    _enableOnly ? item.enable : true,
                );
        },
        defaultValue: [],
    });
    return {
        ...swr,
    };
};

export const useForexAccountList = (options = {}) => {
    const {
        type = null,
        user,
        enableOnly,
        disableOnly,
        disReadonly,
        request = true,
    } = options;
    const {isUser, id} = useAuth();
    const swr = useSWRTemplate({
        key: request
            ? [
                  isUser
                      ? `userForexAccountList/${id}`
                      : 'adminForexAccountList',
                  type,
                  user,
                  enableOnly,
                  disableOnly,
                  disReadonly,
                  isUser,
              ]
            : null,
        request: async ([
            _,
            _type,
            _user,
            _enableOnly,
            _disableOnly,
            _disReadonly,
            _isUser,
        ]) => {
            const {result} = await api().get(
                ['fx', 'account'],
                cleanObjectNilValue({
                    type: _type != 'Hidden' ? _type : null,
                    isHidden: _type == 'Hidden' ? true : _isUser ? false : null,
                    user: _user,
                }),
            );

            return orderBy(
                result,
                (o) =>
                    Number(get(o, 'balance') || 0) -
                    Number(get(o, 'locked') || 0),
                'desc',
            )
                .map(processForexAccountList)
                .filter((item) =>
                    _enableOnly
                        ? item.enable && item.accountType != 'Disabled'
                        : true,
                )
                .filter((item) =>
                    _disableOnly ? !item.enable && item.type !== 'Demo' : true,
                )
                .filter((item) => (_disReadonly ? !item.enableReadonly : true))
                .filter(
                    (item) =>
                        item?.accountType != 'PAMM' &&
                        item?.accountType != 'MAM',
                );
        },
        defaultValue: [],
    });
    return {
        ...swr,
    };
};

export const useIBForexAccountList = (options = {}) => {
    const {type = null, user, enableOnly, disableOnly, disReadonly} = options;
    const {isUser, id} = useAuth();
    const swr = useSWRTemplate({
        key: user
            ? [
                  isUser
                      ? `userIBForexAccountList/${id}`
                      : 'adminIBForexAccountList',
                  type,
                  user,
                  enableOnly,
                  disableOnly,
                  disReadonly,
                  isUser,
              ]
            : null,
        request: async ([
            _,
            _type,
            _user,
            _enableOnly,
            _disableOnly,
            _disReadonly,
            _isUser,
        ]) => {
            const {result} = await api().get(
                ['fx', 'account'],
                cleanObjectNilValue({
                    type: _type != 'Hidden' ? _type : null,
                    isHidden: _type == 'Hidden' ? true : _isUser ? false : null,
                    user: _user,
                }),
            );

            return orderBy(
                result,
                (o) =>
                    Number(get(o, 'balance') || 0) -
                    Number(get(o, 'locked') || 0),
                'desc',
            )
                .map(processForexAccountList)
                .filter((item) =>
                    _enableOnly
                        ? item.enable && item.accountType != 'Disabled'
                        : true,
                )
                .filter((item) =>
                    _disableOnly ? !item.enable && item.type !== 'Demo' : true,
                )
                .filter((item) => (_disReadonly ? !item.enableReadonly : true))
                .filter(
                    (item) =>
                        item?.accountType != 'PAMM' &&
                        item?.accountType != 'MAM',
                );
        },
        defaultValue: [],
    });
    return {
        ...swr,
    };
};

// CRM-758 Rebate 账户选择，与佣金分发（Service IBTree）收款账户解析对齐：
// 1. 用户显式设置的 isDefault 账户优先；
// 2. 无默认时按 source 升序（MT4 优先于 MT4s2/MT4s3），同 source 取最早创建
//    （分发侧同 source 多账户无显式 tie-breaker，实际按主键序即最早账户）；
// 兜底选出的账户不是用户显式默认（isRecommended=true），调用方应提示"系统推荐"，避免误认为已设默认。
const pickRebateAccount = (list, sources) => {
    const rebates = (list || []).filter(
        (item) => sources.includes(item?.accountSource) && item?.type === 'Rebate',
    );
    const defaultAccount = rebates.find((item) => item.isDefault);
    if (defaultAccount) return {account: defaultAccount, isRecommended: false};
    const [fallback] = rebates.slice().sort((a, b) => {
        const bySource = sources.indexOf(a.accountSource) - sources.indexOf(b.accountSource);
        if (bySource !== 0) return bySource;
        return (new Date(a.createdAt) - new Date(b.createdAt)) || (a.id - b.id);
    });
    return {account: fallback, isRecommended: Boolean(fallback)};
};

export const useUserForexAccountList = (options = {}) => {
    const {
        type = null,
        user,
        enableOnly,
        disableOnly,
        disReadonly,
        source,
        onlyRewired,
        isHidden
    } = options;
    const {isUser, id} = useAuth();
    const swr = useSWRTemplate({
        key: user
            ? [
                  isUser
                      ? `userForexAccountList/${id}`
                      : 'adminForexAccountList',
                  type,
                  user,
                  enableOnly,
                  disableOnly,
                  disReadonly,
                  isUser,
                  source,
                  onlyRewired,
                  isHidden
              ]
            : null,
        request: async ([
            _,
            _type,
            _user,
            _enableOnly,
            _disableOnly,
            _disReadonly,
            _isUser,
            _source,
            _onlyRewired,
            _isHidden
        ]) => {
            const {result} = await api().get(
                ['ib', 'account', _user],
                cleanObjectNilValue({type: _type, source: _source, onlyRewired: _onlyRewired, isHidden: _isHidden}),
            );

            return result
                .map(processForexAccountList)
                .filter((item) =>
                    _enableOnly
                        ? item.enable && item.accountType != 'Disabled'
                        : true,
                )
                .filter((item) =>
                    _disableOnly ? !item.enable && item.type !== 'Demo' : true,
                )
                .filter((item) => (_disReadonly ? !item.enableReadonly : true))
                .filter(
                    (item) =>
                        item?.accountType != 'PAMM' &&
                        item?.accountType != 'MAM',
                );
        },
        defaultValue: [],
    });
    const mt4RebatePick = pickRebateAccount(swr.data, ['MT4', 'MT4s2', 'MT4s3']);
    const mt5RebatePick = pickRebateAccount(swr.data, ['MT5']);
    return {
        ...swr,
        MT4Rebate: mt4RebatePick.account,
        MT5Rebate: mt5RebatePick.account,
        isMT4RebateRecommended: mt4RebatePick.isRecommended,
        isMT5RebateRecommended: mt5RebatePick.isRecommended,
    };
};

export const useUserRewiredAccounts = (options = {}) => {
    const {
        type = null,
        user,
        source,
        onlyRewired,
        isHidden
    } = options;
    const {isUser, id} = useAuth();
    const swr = useSWRPaginationTemplate({
        key: user
            ? [
                  isUser
                      ? `userRewireAccountList/${id}`
                      : 'adminRewireAccountList',
                  type,
                  user,
                  source,
                  onlyRewired,
                  isHidden
              ]
            : null,
        request: async ([
            _,
            _type,
            _user,
            _source,
            _onlyRewired,
            _isHidden
        ], _pageIndex, _pageSize) => {
            const {result} = await api().get(
                ['ib', 'account', _user],
                cleanObjectNilValue({type: _type, source: _source, onlyRewired: _onlyRewired, isHidden: _isHidden, pageIndex: _pageIndex - 1, pageSize: _pageSize}),
            );

            return {
                result: {
                    items: get(result, 'items')
                    .map(processForexAccountList)
                    .filter(
                        (item) =>
                            item?.accountType != 'PAMM' &&
                            item?.accountType != 'MAM',
                    ),
                    totalItems: get(result, 'totalItems')
                }
            }
        },
        defaultValue: {
            items: [],
            totalItems: 0
        },
    });
    return {
        ...swr,
    };
};


export const downloadRewireAccount = async (user, username, isClient=false) => {
    const {result} = await api().get(
        ['ib', 'account', user],
        cleanObjectNilValue({
            onlyRewired: true
        }),
    );
    if(!result.length) {
        throw new Error('Can not export empty data')
    }
    const data = result.map(processForexAccountList)
    .filter(
        (item) =>
            item?.accountType != 'PAMM' &&
            item?.accountType != 'MAM',
    )

    const workbook = new Excel.Workbook();
    const sheet = workbook.addWorksheet('Closed orders daily');
    sheet.columns = [
        {header: 'Account type', key: 'accountType'},
        {header: 'Client', key: 'client'},
        ...(isClient? []: [{header: 'Email', key: 'email'}]),
        {header: 'Account num', key: 'login'},
        ...(isClient? []: [{header: 'Group', key: 'group'}]),
        {header: 'Currency', key: 'currency'},
        {header: 'Status', key: 'status'},
        {header: 'Rewired date', key: 'rewiredDate'},
        {header: 'Closed orders', key: 'closedOrders'},
    ];

    console.log('data', data)

    for (const grant of data) {
        const row = [];
        for (const col of sheet.columns) {
            switch (col.key) {
                case 'accountType':
                    row.push(`${convertSource(get(grant, 'accountSource'))} ${get(
                            grant,
                            'type',
                        )}`);
                    break;
                case 'client':
                    row.push(get(grant, 'clientDetail.name'));
                    break;
                case 'email':
                    row.push(get(grant, 'clientDetail.email'));
                    break;
                case 'login':
                    row.push(grant.login);
                    break;
                case 'group':
                    row.push(grant.group);
                    break;
                case 'currency':
                    row.push(grant.currency);
                    break;
                case 'status':
                    row.push(grant.status);
                    break;
                case 'rewiredDate':
                    row.push(formatDateTimes(get(grant, 'rewireUpdateAt'), '', 2, true));
                    break;
                case 'closedOrders':
                    row.push(currencyFormatorWithDecimal(get(grant, 'rawData.volume'), '', 2, true));
                    break;
            }
        }
        sheet.addRow(row);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    let a = window.document.createElement('a');
    a.href = URL.createObjectURL(
        new Blob([buffer], {type: 'application/vnd.openxmlformats'}),
    );
    a.download = `Rewired accounts(${username}).xlsx`;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
};

export const useAccountForexAccountList = (options = {}) => {
    const {id} = useAuth();
    const swr = useSWRTemplate({
        key: id ? ['useAccountForexAccountList', id] : null,
        request: async ([_, _id]) => {
            const {result} = await api().get(['fx', 'account-group']);

            const odj = {};
            for (const key in result) {
                if (Object.prototype.hasOwnProperty.call(result, key)) {
                    const element = result[key];
                    odj[key] = orderBy(
                        element,
                        (o) =>
                            Number(get(o, 'balance') || 0) -
                            Number(get(o, 'locked') || 0),
                        'desc',
                    ).map(processForexAccountList);
                }
            }
            return odj;
        },
        defaultValue: {
            Live: [],
            Rebate: [],
            Demo: [],
            Hidden: [],
            Disabled: [],
            Archived: [],
        },
    });
    return {
        ...swr,
    };
};

export const useForexAccountDetail = (options = {}) => {
    const {id, request = true} = options;
    const swr = useSWRTemplate({
        key: id && request ? ['forexAccountDetail', id] : null,
        request: async ([_, _id]) => {
            const {result} = await api().get(['fx', 'account', _id]);
            return processForexAccountList(result);
        },
        defaultValue: {},
    });
    return {
        ...swr,
    };
};

export const editMtRebateAccount = async (data) => {
    const {result} = await api().post(
        ['ib', 'rebate-account'],
        cleanObjectNilValue(data),
    );
    return result;
};

export const editMtUpRebateAccount = async (data) => {
    const {result} = await api().post(
        ['ib', 'up-rebate-account'],
        cleanObjectNilValue(data),
    );
    return result;
};

export const useForexTransaction = (options = {}) => {
    const {startDate, endDate, accountId, status, type, ticket, user} = options;
    const formattedDateRange = formatedStartEndDate({
        startDate,
        endDate,
    });

    const swr = useSWRPaginationTemplate({
        key: [
            'forexTransaction',
            formattedDateRange.startDate,
            formattedDateRange.endDate,
            accountId,
            status,
            type,
            ticket,
            user,
        ],
        request: async (_key, _pageIndex, _pageSize) => {
            const [
                _,
                _startDate,
                _endDate,
                _accountId,
                _status,
                _type,
                _ticket,
                _user,
            ] = _key;
            const {result} = await api().get(
                ['transaction'],
                cleanObjectNilValue({
                    pageIndex: _pageIndex,
                    pageSize: _pageSize,
                    startDate: _startDate,
                    endDate: _endDate,
                    accountId: _accountId,
                    status: _status,
                    type: _type,
                    ticket: _ticket,
                    user: _user,
                }),
            );

            return {
                result: {
                    totalItems: get(result, 'totalItems'),
                    items: get(result, 'items'),
                },
            };
        },
        defaultValue: [],
    });
    return {
        ...swr,
    };
};

export const useFXDashboard = (options = {}) => {
    const {user = null} = options;
    const {isUser, id} = useAuth();
    const swr = useSWRTemplate({
        key: id ? ['FXDashboard', id, user] : null,
        request: async ([_, _id, _user]) => {
            const {result} = await api().get(
                [isUser ? 'fx' : 'summary', 'dashboard'],
                cleanObjectNilValue({
                    user: _user,
                }),
            );
            return result;
        },
        defaultValue: {},
    });
    return {
        ...swr,
    };
};
export const useUserUpperIb = (options = {}) => {
    const {user = null} = options;
    const swr = useSWRTemplate({
        key: user ? ['user/upper-ib', user] : null,
        request: async ([_, _user]) => {
            const {result} = await api().get(
                ['user', 'upper-ib'],
                cleanObjectNilValue({
                    user: _user,
                }),
            );
            return result;
        },
        defaultValue: {},
    });
    return {
        ...swr,
    };
};

export const useFundingDashboard = (options = {}) => {
    const {isUser, id} = useAuth();
    const swr = useSWRTemplate({
        key: isUser && id ? ['fundingDashboard', id] : null,
        request: async ([_, _id]) => {
            const {result} = await api().get(['funding', 'dashboard']);
            return result;
        },
        defaultValue: {},
    });
    return {
        ...swr,
    };
};

export const useLeverages = (options = {}) => {
    const {isUser} = useAuth();
    const swr = useSWRTemplate({
        key: isUser ? ['leverages'] : null,
        request: async (_) => {
            const {result} = await api().get(['fx', 'leverage-setting']);
            return (result || []).map((item) => {
                return {
                    label: `1:${item}`,
                    value: item,
                };
            });
        },
        defaultValue: [],
    });

    return {
        ...swr,
    };
};

const depositWayOrder = [
    'Worldpay',
    'creditCardOrDebitCard',
    'Local Bank Transfer',
    'SWIFT Bank Transfer',
    'Local Bank Transfer(Shares)',
    'SWIFT Bank Transfer(Shares)',
    'Skrill',
    'NACE Pay',
    'cryptocurrencies',
    'cryptoCurrencies',
    'PayGo',
    'Web3.0',
    'Awepay',
    'Fasapay',
    'Dragonpay',
    'USDT',
    'Bitwallet',
    'ExLink',
    'Korean Solution',
    'Finmo',
    'Finmo Online',
    'Acxpress',
    'sgdPayNowLocalTransfer',
    'Customer Support',
];

export const useDepositWay = (options = {}) => {
    const {data: preference} = useUserPreference();
    const swr = useSWRTemplate({
        key: ['depositWay'],
        request: async ([_]) => {
            const {result} = await api().get(['deposit', 'payment-gateway']);
            const data =  flatten(
                (result || [])
                    .map((item) => ({
                        ...item,
                        img:
                            item?.displayName === 'Credit/Debit Card'
                                ? 'VisaMaster'
                                : item?.displayName === 'Bank Transfer'
                                ? 'BankTransfer'
                                : item?.displayName === 'Web3.0'
                                ? 'USDT'
                                : item?.displayName === 'Customer support'
                                ? 'CustomerSupport'
                                : item?.displayName === 'Local Finmo'
                                ? 'Local Bank Transfer'
                                : item?.displayName === 'Skrill'
                                ? 'skrill'
                                : item?.displayName,
                        channel: paymentWaysNameChannelMap[item.displayName],
                        // displayName: item.displayName==='Finmo'? 'PayNow': item.displayName,
                        displayName: item.displayName==='Finmo'? 'sgdPayNowLocalTransfer': item.displayName === 'Bank Transfer' ? 'SWIFT Bank Transfer' : item.displayName === 'Coin Flux' ? 'cryptoCurrencies' : item.displayName,
                    }))
                    .map((item) => {
                        if (item.channel === 'nacepay') {
                            return [
                                item,
                                {
                                    ...item,
                                    channel: 'nacepayWidget',
                                    // displayName: 'nacepayWidget',
                                    displayName: 'cryptocurrencies',
                                },
                            ];
                        } else if (item.channel ==='worldpay' && preference?.enableWorldpay3DS) {
                            return [
                                item,
                                {
                                    ...item,
                                    channel: 'worldpay3ds',
                                    // displayName: 'worldpay3ds',
                                    displayName: 'creditCardOrDebitCard',
                                },
                            ];
                           
                        }
                        else {
                            return item;
                        }
                    }),
            )
                .filter((item) => !gatewayDisabled.includes(item.channel))
                // .filter((item) => item.channel !== 'worldpay')
                
                .sort((a, b) => depositWayOrder.indexOf(a.displayName) -  depositWayOrder.indexOf(b.displayName))
            // const skrillIndex = data.findIndex(item => item.channel === 'skrill');
            // const localEftIndex = data.findIndex(item => item.channel === 'localEft');
            // if(skrillIndex !== -1 && skrillIndex !== 4) {
            //     const [skrillItem] = data.splice(skrillIndex, 1);
            //     data.splice(4, 0, skrillItem);
            // }
            // if(localEftIndex !== -1 && localEftIndex !== 3) {
            //     const [localEftItem] = data.splice(localEftIndex, 1);
            //     data.splice(2, 0, localEftItem);
            // }
            return data
        },

        defaultValue: [],
    });
    return {
        ...swr,
    };
};

export const useAccountGroup = (options = {}) => {
    const swr = useSWRTemplate({
        key: ['account-group'],
        request: async ([_]) => {
            const {result} = await api().get(['commons', 'account-group']);
            return result;
        },
        defaultValue: {},
    });
    return {
        ...swr,
    };
};

export const useCloseOrders = (options = {}) => {
    const {
        status,
        startDate,
        endDate,
        accountId,
        userId,
        pageSize = 100,
        request = true,
    } = options;
    const swr = useSWRPaginationTemplate({
        key:
            accountId && request
                ? ['closeOrders', status, startDate, endDate, accountId, userId]
                : null,
        pageSize: pageSize,
        request: async (_key, _pageIndex, _pageSize) => {
            const [_, _status, _startDate, _endDate, _accountId, _userId] =
                _key;

            const query = {
                startDate: _startDate
                    ? formatDateInTimezone(_startDate, 'UTC', 'yyyy-MM-dd')
                    : null,
                endDate: _endDate
                    ? formatDateInTimezone(_endDate, 'UTC', 'yyyy-MM-dd')
                    : null,
                accountId: _accountId,
                userId: Number(_userId) || null,
                pageSize: _pageSize,
                pageIndex: _pageIndex - 1,
            };
            const {result} = await api().get(
                ['fx', 'close-order'],
                cleanObjectNilValue(query),
            );
            return {
                result: {
                    totalItems: get(result, 'totalItems'),
                    items: get(result, 'items').map(processClosedOrders),
                },
            };
        },
        defaultValue: {
            totalItems: 0,
            items: [],
        },
    });
    return swr;
};

export const downloadUserCloseOrders = async (options = {}, isUser) => {
    const {accountId, startDate, endDate,userId} = options;
    const {result} = await api().get(
        ['fx', 'close-order'],
        cleanObjectNilValue({
            ...(isUser?{}: { userId: userId }),
            accountId: accountId,
            startDate: startDate ? formatMonthDataAndYear(startDate) : null,
            endDate: endDate ? formatMonthDataAndYear(endDate) : null,
        }),
    );
    if(!result.length) {
        throw new Error('Can not export empty data')
    }

    const workbook = new Excel.Workbook();
    const sheet = workbook.addWorksheet('Close orders');
    sheet.columns = [
        {header: 'Last update on', key: 'lastUpdateOn'},
        {header: 'Account num', key: 'login'},
        {header: 'Symbol', key: 'symbol'},
        {header: 'Closed lots', key: 'closedLots'},
        {header: 'Open price', key: 'openPrice'},
        {header: 'Close price', key: 'closePrice'},
        {header: 'CMD', key: 'cmd'},
        {header: 'Profits', key: 'profit'},
    ];

    for (const grant of result) {
        const row = [];
        for (const col of sheet.columns) {
            switch (col.key) {
                case 'lastUpdateOn':
                    const date = get(grant, 'closeTime') 
                    row.push(date?formatDateInTimezone(date, 'UTC'): '');
                    break;
                case 'login':
                    row.push(grant.login);
                    break;
                case 'symbol':
                    row.push(grant.symbol);
                    break;
                case 'closedLots':
                    row.push(currencyFormatorWithDecimal(grant.volume, ''));
                    break;
                case 'openPrice':
                    const digits = get(grant, 'digits');
                    row.push(numeral(get(grant, 'openPrice')).format(
                        digits
                            ? `0,0.${new Array(digits).fill(0).join('')}`
                            : '0,0.00',
                    ));
                    break;
                case 'closePrice':
                    const digits2 = get(grant, 'digits');
                    row.push(numeral(get(grant, 'closePrice')).format(
                        digits2
                            ? `0,0.${new Array(digits2).fill(0).join('')}`
                                : '0,0.00',
                        ));
                    break;
                case 'cmd':
                row.push(Boolean(get(grant, 'cmd')) ? 'Sell' : 'Buy');
                    break;
                case 'profit':
                row.push(currencyFormatorWithDecimal(grant.profit, ''));
                    break;
            }
        }
        sheet.addRow(row);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    let a = window.document.createElement('a');
    a.href = URL.createObjectURL(
        new Blob([buffer], {type: 'application/vnd.openxmlformats'}),
    );
    a.download = `Closed orders.xlsx`;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
};

export const useFXClosedTrends = (options = {}) => {
    const {type, login} = options;
    const swr = useSWRTemplate({
        key: type ? ['FXClosedTrends', type, login] : null,
        request: async ([_, _type, _login]) => {
            const {result} = await api().get(
                ['fx', 'trends'],
                cleanObjectNilValue({
                    type: _type,
                    login: _login,
                    cycle: 'month',
                }),
            );
            return result;
        },
        defaultValue: [],
    });
    return {
        ...swr,
    };
};

export const useFXBalanceTrends = (options = {}) => {
    const {yearMonth, accountId, user = null} = options;
    const swr = useSWRTemplate({
        key: ['FXBalanceTrends', yearMonth, accountId, user],
        request: async ([_, _yearMonth, _accountId, _user]) => {
            const query = {
                yearMonth: _yearMonth,
                accountId: _accountId,
                user: _user,
            };
            const {result} = await api().get(
                ['summary', 'balance-chart'],
                cleanObjectNilValue(query),
            );
            return result;
        },
        defaultValue: [],
    });
    return {
        ...swr,
        monthList: swr.data?.months?.map((item) => {
            return {
                label: item,
                value: item,
            };
        }),
    };
};
export const useProfitsTrends = (options = {}) => {
    const {yearMonth, accountId, user} = options;
    const swr = useSWRTemplate({
        key: ['FXProfitsTrends', yearMonth, accountId, user],
        request: async ([_, _yearMonth, _accountId, _user]) => {
            const query = {
                yearMonth: _yearMonth,
                accountId: _accountId,
                user: _user,
            };
            const {result} = await api().get(
                ['summary', 'profits-chart'],
                cleanObjectNilValue(query),
            );
            return result;
        },
        defaultValue: [],
    });
    return {
        ...swr,
        data: swr.data,
        monthList: swr.data?.months?.map((item) => {
            return {
                label: item,
                value: item,
            };
        }),
    };
};

export const useClosedLotsTrends = (options = {}) => {
    const {yearMonth, accountId, user = null} = options;
    const swr = useSWRTemplate({
        key: ['closedLotsTrends', yearMonth, accountId, user],
        request: async ([_, _yearMonth, _accountId, _user]) => {
            const query = {
                yearMonth: _yearMonth,
                accountId: _accountId,
                user: _user,
            };
            const {result} = await api().get(
                ['summary', 'closed-order-chart'],
                cleanObjectNilValue(query),
            );
            return result;
        },
        defaultValue: [],
    });
    return {
        ...swr,
        data: swr.data,
        monthList: swr.data?.months?.map((item) => {
            return {
                label: item,
                value: item,
            };
        }),
    };
};

export const useTradesTrends = (options = {}) => {
    const {yearMonth, type, user = null, request = true} = options;
    const swr = useSWRTemplate({
        key: request ? ['tradesTrends', yearMonth, type, user] : null,
        request: async ([_, _yearMonth, _type, _user]) => {
            const query = {
                yearMonth: _yearMonth,
                user: _user,
            };
            const {result} = await api().get(
                ['summary', `${_type}-chart`],
                cleanObjectNilValue(query),
            );
            return {
                ...result,
                data: get(result, 'data').map((item) => {
                    return {
                        ...item,
                        amount:
                            type === 'deposit'
                                ? get(item, 'deposit')
                                : type === 'withdrawal'
                                ? get(item, 'withdrawal')
                                : get(item, 'commission'),
                    };
                }),
            };
        },
        defaultValue: [],
    });
    return {
        ...swr,
        data: reverse(swr.data),
        monthList: swr.data?.months?.map((item) => {
            return {
                label: item,
                value: item,
            };
        }),
    };
};

export const useCurrencyRates = (options = {}) => {
    const {isUser} = useAuth();
    const swr = useSWRTemplate({
        key: isUser ? ['currency-rates'] : null,
        request: async ([_]) => {
            const {result} = await api().get(['commons', 'currency-rates']);
            return result;
        },
        defaultValue: {},
    });
    return {
        ...swr,
    };
};

export const useFXGroups = (options = {}) => {
    const {id, source, request = true} = options;
    const {isUser} = useAuth();
    const swr = useSWRTemplate({
        key:
            !isUser && id && source && request
                ? ['fx-groups', id, source]
                : null,
        request: async ([_, _id, _source]) => {
            const {result} = await api().get([
                String(_source).startsWith('MT4') ? 'mt4' : 'mt5',
                'groups',
                _id,
            ]);
            return result;
        },
        defaultValue: [],
    });

    return {
        ...swr,
    };
};

export const changeForexPassword_ = async (data) => {
    const res = await api().post(['fx', 'password'], data);
    return res;
};

export const changeLeverage_ = async (data) => {
    const res = await api().post(['fx', 'leverage'], data);
    return res;
};

export const changeGroup_ = async (id, data) => {
    const res = await api().put(['fx', 'account', id], data);
    return res;
};

export const hiddenForexAccount_ = async (data) => {
    const res = await api().post(['fx', 'hidden'], data);
    return res;
};

export const suspendForexAccount_ = async (id, data) => {
    const res = await api().put(['fx', 'account', id], data);
    return res;
};

export const addApplyAccount_ = async (data) => {
    const res = await api().post(['fx', 'account'], cleanObjectNilValue(data));
    return res;
};

export const changeClientLeverage_ = async (id, data) => {
    const res = await api().post(['fx', 'leverage', id], data);
    return res;
};

export const useSynchronizeAccounts = () => {
    const swr = useSWRTemplate({
        key: ['fx/synchronize-accounts'],
        request: async () => {
            const {result} = await api().get(['fx', 'sync-account']);
            return result;
        },
        defaultValue: [],
    });
    return swr;
};
export const synchronizeAccounts = async (logins = []) => {
    const {result} = await api().post(['fx', 'sync-account'], {
        logins,
    });
    return result;
};

export const fxDemoTopUp_ = async (data) => {
    const res = await api().post(['fx', 'demo-deposit'], data);
    return res;
};

export const useSelectAccountList = (options = {}) => {
    const {user} = options;
    const {isUser, id} = useAuth();
    const swr = useSWRTemplate({
        key: user ? ['adminForexAccountList', user] : null,
        request: async ([
            _,
            _user,
            _enableOnly = true,
            _disableOnly,
            _disReadonly,
            _isUser,
        ]) => {
            const {result} = await api().get(
                ['commons', 'account-list'],
                cleanObjectNilValue({
                    type: 'Live',
                    userId: _user,
                }),
            );

            return orderBy(
                result,
                (o) =>
                    Number(get(o, 'balance') || 0) -
                    Number(get(o, 'locked') || 0),
                'desc',
            ).map((item) => {
                return {
                    label: `${get(item, 'source')}-${get(item, 'login')}`,
                    value: get(item, 'id'),
                    source: get(item, 'source'),
                };
            });
        },
        defaultValue: [],
    });
    return {
        ...swr,
    };
};

export const useSelectLeverages = (options = {}) => {
    const {user} = options;
    const swr = useSWRTemplate({
        key: user ? ['leverages', user] : null,
        request: async ([_, _user]) => {
            const {result} = await api().get(
                ['commons', 'leverage-setting'],
                cleanObjectNilValue({userId: _user}),
            );
            return (result || []).map((item) => {
                return {
                    label: `1:${item}`,
                    value: Number(item),
                };
            });
        },
        defaultValue: [],
    });

    return {
        ...swr,
    };
};

export const useClientForexAccountList = (options = {}) => {
    const {user} = options;
    const swr = useSWRTemplate({
        key: user ? ['useClientForexAccountList', user] : null,
        request: async ([_, _user]) => {
            const {result} = await api().get(['fx', 'account-group'], {
                user: _user,
            });
            const odj = {};
            for (const key in result) {
                if (Object.prototype.hasOwnProperty.call(result, key)) {
                    const element = result[key];
                    odj[key] = orderBy(
                        element,
                        (o) =>
                            Number(get(o, 'balance') || 0) -
                            Number(get(o, 'locked') || 0),
                        'desc',
                    ).map(processForexAccountList);
                }
            }
            return odj;
        },
        defaultValue: {
            Live: [],
            Rebate: [],
            Demo: [],
            Hidden: [],
            Disabled: [],
            Archived: [],
        },
    });
    return {
        ...swr,
    };
};

export const useDefaultGroupSettings = (options = {}) => {
    const {type} = options;
    const swr = useSWRTemplate({
        key: type ? ['forex/defaultGroupSettings', type] : null,
        request: async ([_, _type]) => {
            const {result} = await api().get(
                ['forex', 'default-group-setting'],
                {type: _type},
            );
            const res = flatten(
                JSON.parse(get(result, 'value') || '[]').map((item) => {
                    return keys(item).map((key) => ({
                        currency: key,
                        ...item[key],
                    }));
                }),
            );
            return res;
        },
        defaultValue: [],
    });
    return swr;
};

// 获取当前用户上级IB的分组配置
export const useIBGroupSetting = () => {
    const swr = useSWRTemplate({
        key: ['forex/ibGroupSetting'],
        request: async () => {
            const {result} = await api().get(['forex', 'ib-group-setting']);
            return result;
        },
        defaultValue: {},
    });
    return swr;
};

export const useIBDefaultGroupSettings = (options = {}) => {
    const {type, request = true} = options;
    const swr = useSWRTemplate({
        key: type && request ? ['useIBDefaultGroupSettings', type] : null,
        request: async ([_, _type]) => {
            const {result} = await api().get(
                ['forex', 'default-group-setting'],
                {type: _type},
            );
            return result;
        },
        defaultValue: [],
    });
    return {
        ...swr,
        mt4GroupList: flatten(
            JSON.parse(get(swr, 'data')?.[0]?.['value'] || '[]').map((item) => {
                return keys(item).map((key) => ({
                    currency: key,
                    ...item[key],
                }));
            }),
        ),
        mt5GroupList: flatten(
            JSON.parse(get(swr, 'data')?.[1]?.['value'] || '[]').map((item) => {
                return keys(item).map((key) => ({
                    currency: key,
                    ...item[key],
                }));
            }),
        ),
    };
};

export const useAdminAllFXGroup = (options = {}) => {
    const swr = useSWRTemplate({
        key: ['AdminAllFXGroup'],
        request: async ([_]) => {
            const {result} = await api().get(['fx', 'groups']);
            return result;
        },
        defaultValue: [],
    });

    return {
        ...swr,
    };
};

export const useAdminDefaultGroupSettings = (options = {}) => {
    const {type} = options;
    const swr = useSWRTemplate({
        key: type ? ['forex/defaultGroupSettings', type] : null,
        request: async ([_, _type]) => {
            const {result} = await api().get(['fx', 'default-group-setting'], {
                type: _type,
            });
            const res = flatten(
                JSON.parse(get(result, 'value') || '[]').map((item) => {
                    return keys(item).map((key) => ({
                        currency: key,
                        ...item[key],
                    }));
                }),
            );
            return res;
        },
        defaultValue: [],
    });
    return swr;
};

export const useUplevelIBGroupCurrencies = (options = {}) => {
    const {user, source} = options;
    const swr = useSWRTemplate({
        key: user && source ? ['uplevelIBGroupCurrencies', user, source] : null,
        request: async ([_, _user, _source]) => {
            const {result} = await api().get(['support', 'apply-account'], {
                userId: _user,
                source: _source,
            });
            return result;
        },
        defaultValue: null,
    });
    return swr;
};

export const useAdminLeverages = (options = {}) => {
    const {userId} = options;
    const {isAdmin} = useAuth();
    const swr = useSWRTemplate({
        key: userId ? ['AdminLeverages', userId] : null,
        request: async ([_, _userId]) => {
            const {result} = await api().get(['commons', 'leverage-setting'], {
                userId: _userId,
            });
            return (result || []).map((item) => {
                return {
                    label: `1:${item}`,
                    value: item,
                };
            });
        },
        defaultValue: [],
    });

    return {
        ...swr,
    };
};


export const useMtInfo = () => {
    const swr = useSWRTemplate({
        key: ['mt-info'],
        request: async () => {
            const {result} = await api().get(['mt-info'])
            return result
        },
        defaultValue: {}
    })
    return swr
}

export const resetSharePassword = async (data) => {
    await api().post(['stock', 'reset-password'], data)
}

export const useUserRefunds = (userId) => {
    const swr = useSWRTemplate({
        key: ['client/refunds', userId],
        request: async () => {
            const {result} = await api().get(['summary', 'worldpay'], cleanObjectNilValue({ userId }))
            return result
        },
        defaultValue: []
    })
    return swr
}

export const applySKrillAccount = async (email) => {
    const {result} = await api().post(['wallet', 'skrill'], cleanObjectNilValue({ email }));
    return result;
}
